import { useMutation, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/api/client';
import { useTenantStore } from '@/stores/tenantStore';

import { sessionKeys } from './sessionKeys';
import { inviteKeys } from './useListWorkflowInvites';

/** One recipient of a send. At least one of the two must be present. */
export interface InviteRecipientInput {
  email?: string | null;
  mobile?: string | null;
}

export interface SendWorkflowInvitesInput {
  workflowId: string;
  recipients: InviteRecipientInput[];
  /** The drawer's "Session Id" — applied to every recipient in this send. */
  referenceId?: string | null;
}

export interface WorkflowInvite {
  id: string;
  email: string | null;
  mobile: string | null;
  referenceId: string | null;
  sentAt: string;
  sendFailed: boolean;
  status: string;
}

export interface SendWorkflowInvitesResult {
  batchId: string;
  /** Everyone asked for, including the skipped — so "8 of 10" adds up. */
  total: number;
  sent: number;
  /** Recipients whose email bounced at the gateway — their rows still exist. */
  failed: number;
  /**
   * Recipients already invited to this workflow, so no second link went out.
   * One person gets the link once per workflow.
   */
  skipped: number;
  invites: WorkflowInvite[];
}

/**
 * Records "we sent this workflow's link to these people" and emails each of them
 * the workflow's COMMON link, tagged with their own tracking code.
 *
 * Creates NO sessions — a session appears only when a recipient verifies a mobile
 * number by OTP. That is why the Sessions list stays a record of real runs, and
 * this is a separate record of who was asked.
 *
 * Copy link and the QR are unaffected: they share the same common link and record
 * nothing, exactly as before.
 */
export function useSendWorkflowInvites() {
  const qc = useQueryClient();
  const tenantId = useTenantStore((s) => s.tenantId);

  return useMutation({
    mutationFn: async (body: SendWorkflowInvitesInput) => {
      return (await apiClient
        .post(`api/v1/t/${tenantId}/invites`, { json: body })
        .json()) as SendWorkflowInvitesResult;
    },
    onSuccess: () => {
      // A newly sent invite appears in the SESSIONS list as a Not Started row, so that
      // is the list that must refresh for it to show up without a manual reload. The
      // invite keys are invalidated too, for anything reading invites directly.
      qc.invalidateQueries({ queryKey: inviteKeys.all });
      qc.invalidateQueries({ queryKey: sessionKeys.lists() });
    },
  });
}
